/* eslint-disable no-restricted-syntax */
// import { LatLng, icon } from 'leaflet';
import moment from 'moment';

import { generateTimestampsInSeconds } from '@/util/date';
import {
  parseTrailResponse, trailRangeFrom, hourWindow, rollupPointDetail,
} from '@/util/trailData';
import { deriveRpmTypesFromSummary, deriveAeCountFromSummary } from '@/util/seriesToSummaryData';
import { fetchSummarySeriesData } from '@/util/summarySeries';

import AssetSvc from '@/services/AssetSvc';
import PulseSvc from '@/services/PulseSvc';
import SummarySvc from '@/services/SummarySvc';
import SeriesSvc from '@/services/SeriesSvc';
import DeviceSvc from '@/services/DeviceSvc';
// eslint-disable-next-line no-unused-vars
import LandmarkSvc from '@/services/LandmarkSvc';

import { buildIconConfig } from '@/util/shipIcons';

import {
  getAllVisibilityStatuses,
  saveVisibilityStatus,
  saveAllVisibilityStatuses,
} from '@/services/dbService';

const state = {
  assets: [],
  liveTrackingId: null,
  isLiveTrackingActive: false,
};

// Tipe sensor "kesehatan" yang menentukan status no-sensor (selain GPS).
// Aset hanya dianggap bermasalah bila sensor yang MEMANG dimilikinya diam.
const HEALTH_SENSOR_TYPES = ['rpm', 'flowmeter', 'ae', 'temperature'];

// Group flowmeter yang TIDAK ditampilkan di dashboard FMS.
// BUNKER = jalur isi BBM (ranah bunker dashboard), bukan konsumsi engine.
const EXCLUDED_FLOWMETER_GROUPS = ['BUNKER'];

function convertDecimalToDMS(decimal, isLatitude) {
  const degrees = Math.floor(Math.abs(decimal));
  const minutesDecimal = (Math.abs(decimal) - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = Math.round((minutesDecimal - minutes) * 60);

  // eslint-disable-next-line no-nested-ternary
  const direction = decimal >= 0
    ? (isLatitude ? 'N' : 'E')
    : (isLatitude ? 'S' : 'W');

  return `${degrees}° ${minutes}' ${seconds}'' ${direction}`;
}

const processSummarySeriesData = (summary, firstQueryTimestamp, lastQueryTimestamp) => {
  // Bucket logic:
  // We generate a list of timestamps (buckets) between the requested start and end
  // using a fixed interval (3600 seconds = 1 hour). Server returns aggregated
  // data keyed by timestamp; we round those keys into the same hourly buckets
  // so each bucket represents data for that hour.
  const timestamps = generateTimestampsInSeconds(firstQueryTimestamp, lastQueryTimestamp, 3600);

  const roundedData = (data) => {
    const rounded = {};
    Object.keys(data || {}).forEach((key) => {
      // Round raw timestamps into the hourly bucket they belong to
      const roundedKey = Math.round(key / 3600) * 3600;
      rounded[roundedKey] = data[key];
    });
    return rounded;
  };

  const roundedGps = roundedData(summary.gps?.data);
  const roundedRpm = roundedData(summary.rpm?.data);
  const roundedFm = roundedData(summary.flowmeter?.data);
  const roundedAe = roundedData(summary.ae?.data);

  return timestamps.map((timestamp) => {
    const gpsData = roundedGps[timestamp] || {};
    const rpmData = roundedRpm[timestamp] || {};
    const fmData = roundedFm[timestamp] || {};
    const aeData = roundedAe[timestamp] || {};

    const latLong = [gpsData?.additional?.LAST_dlgpLatitude_4, gpsData?.additional?.LAST_dlgpLongitude_5];
    const totalCruise = gpsData?.additional?.SUM_dlgpDistance_3;
    const avgSpeed = gpsData.speed;
    const currentAe = Object.values(aeData).find((v) => v.rpm > 0)?.no + 1;
    const portRpm = rpmData.PORT?.rpm;
    const starboardRpm = rpmData.STARBOARD?.rpm;
    const centerRpm = rpmData.CENTER?.rpm;
    const portInFlowrate = fmData.PORT_Flow;
    const starboardInFlowrate = fmData.STARBOARD_Flow;

    return {
      gpsData,
      rpmData,
      fmData,
      aeData,

      dateTime: moment.unix(timestamp).format('DD-MM-YYYY HH:mm:ss'),
      timestamp,
      latLong,
      heading: gpsData.track,
      totalCruise,
      avgSpeed,
      currentAe,
      portRpm,
      starboardRpm,
      centerRpm,
      portInFlowrate,
      starboardInFlowrate,
    };
  });
};

// Buat role GPS Viewer: trail cuma dari endpoint GPS-only (/series/aggregated/gps/period,
// yang sama dipake halaman Device Detail), bukan /summary/aggregated/period yang bundle
// semua devcType (butuh permission RPM/AE/flowmeter yang gak dipunya role ini). Shape
// response-nya beda dari processSummarySeriesData (array `series` per titik, bukan dict
// per-devcType), jadi dipetain terpisah - field non-GPS (RPM/AE/fuel) sengaja kosong.
const mapGpsSeriesToTrail = (series) => {
  const points = series.map((point) => ({
    latLong: [point.latitude, point.longitude],
    heading: point.track,
    timestamp: point.timestamp,
    gpsData: {
      timestamp: point.timestamp, datetime: point.datetime, speed: point.speed, track: point.track,
    },
  }));
  return {
    coordinates: points.map((p) => p.latLong),
    coordinatesData: points,
  };
};

const fetchGpsOnlyTrail = async (asset, firstQueryTimestamp, lastQueryTimestamp) => {
  const gpsDevice = (asset.devices || []).find((d) => d.devcType === 'gps');
  if (!gpsDevice) return { coordinates: [], coordinatesData: [] };

  const seriesResponse = await SeriesSvc.getAggregatedDataByPeriod('gps', firstQueryTimestamp, lastQueryTimestamp, {
    params: { device: [gpsDevice.devcUniqueId], aggregatedUnit: 'HOUR' },
  });
  const series = seriesResponse?.data?.data?.series || [];
  return mapGpsSeriesToTrail(series);
};

const mapGpsPulseToAssets = async (currAssets, massDevicesMap, gpsPulseDatas, currTime, mode = 'standard') => Promise.all(
  currAssets.map(async (asset) => {
    const newAsset = { ...asset };
    const assetDevices = massDevicesMap.get(asset.massId) || [];
    newAsset.isVisible = true;
    newAsset.locationInfo = null;

    // sat mode: Use massSateliteId to match GPS data
    const isSatMode = mode === 'sat';
    let gpsData;

    if (isSatMode) {
      // Match by massSateliteId
      gpsData = gpsPulseDatas.find((d) => d?.deviceId?.toLowerCase() === asset.massSateliteId?.toLowerCase());
    } else {
      // Standard mode: Match by device mapping
      const gpsDevice = assetDevices.find((v) => v.devcType === 'gps');
      if (gpsDevice) {
        gpsData = gpsPulseDatas.find((d) => d?.deviceId?.toLowerCase() === gpsDevice.devcUniqueId.toLowerCase());
      }
    }

    if (gpsData) {
      const gpsDiffTime = parseInt(currTime) - parseInt(gpsData.timestamp);
      newAsset.trail = false;
      newAsset.dotTrail = true;
      newAsset.lastGpsData = gpsData;
      newAsset.lastGpsDataPulse = gpsData;
      newAsset.lastCoordinate = `${convertDecimalToDMS(gpsData.latitude, true)}, ${convertDecimalToDMS(gpsData.longitude, false)}`;
      // eslint-disable-next-line no-undef
      newAsset.lastLatLng = L.latLng(gpsData.latitude, gpsData.longitude);
      newAsset.dataStatus = gpsDiffTime < 600;
      newAsset.gpsDiffTime = gpsDiffTime; // Store GPS diffTime for later use

      // Determine ship status: online, offline, no-plc (GPS ok but no PLC data)
      // Default to online/offline based on GPS recency, will be updated later if no PLC
      const isGpsOnline = gpsDiffTime < 600;
      newAsset.shipStatus = isGpsOnline ? 'online' : 'offline';

      const { iconConfig, markerIconUrl } = buildIconConfig(asset, newAsset.shipStatus);
      newAsset.iconConfig = iconConfig;
      newAsset.markerIconUrl = markerIconUrl;

      try {
        const locateResponse = await LandmarkSvc.landmarks.locate({
          latitude: gpsData.latitude,
          longitude: gpsData.longitude,
        });
        newAsset.locationInfo = locateResponse?.data?.data || null;
      } catch (error) {
        console.warn('Failed to fetch vessel location:', error);
      }
    } else {
      // No GPS data - set status to no-gps
      newAsset.shipStatus = 'no-gps';
      newAsset.dataStatus = false;

      const { iconConfig, markerIconUrl } = buildIconConfig(asset, 'no-gps');
      newAsset.iconConfig = iconConfig;
      newAsset.markerIconUrl = markerIconUrl;

      console.warn(`No GPS data found for asset: ${asset.massId}`);
    }
    return newAsset;
  }),
);

const mapOtherPulseToAssets = (
  assetsWithGps,
  massDevicesMap,
  aePulseDatas,
  rpmPulseDatas,
  fmPulseDatas,
  temperaturePulseDatas,
  powermeterPulseDatas,
  currTime,
  isSatMode = false,
  deniedSensorTypes = [],
) => assetsWithGps.map((asset) => {
  const newAsset = { ...asset };
  const assetDevices = massDevicesMap.get(asset.massId) || [];
  // PulseSvc.getPulse balikin null (bukan []) kalau ditolak izin (errorCode 412) -
  // normalize di sini biar .filter() di bawah gak error buat role dengan akses terbatas.
  aePulseDatas = aePulseDatas || [];
  rpmPulseDatas = rpmPulseDatas || [];
  fmPulseDatas = fmPulseDatas || [];
  temperaturePulseDatas = temperaturePulseDatas || [];
  powermeterPulseDatas = powermeterPulseDatas || [];

  // sat mode: Use massSateliteId to match AE data
  if (isSatMode && asset.massSateliteId) {
    const aeData = aePulseDatas.filter((d) => d.deviceId === asset.massSateliteId);
    if (aeData && aeData.length > 0) newAsset.lastAeData = aeData;
  } else {
    // Standard mode: Use device mapping
    const aeDevice = assetDevices.find((v) => v.devcType === 'ae');
    if (aeDevice) {
      const aeData = aePulseDatas.filter((d) => d.deviceId === aeDevice.devcUniqueId);
      if (aeData && aeData.length > 0) newAsset.lastAeData = aeData;
    }
  }

  const rpmDevice = assetDevices.find((v) => v.devcType === 'rpm');
  if (rpmDevice) {
    const rpmData = rpmPulseDatas.filter((d) => d.deviceId === rpmDevice.devcUniqueId);
    if (rpmData && rpmData.length > 0) {
      newAsset.lastRpmData = rpmData;
    }
  }

  const fmDeviceIds = assetDevices.filter((v) => v.devcType === 'flowmeter').map((v) => v.devcUniqueId);
  if (fmDeviceIds.length > 0) {
    const fmData = fmPulseDatas.filter((d) => fmDeviceIds.includes(d.deviceId));
    if (fmData && fmData.length > 0) newAsset.lastFmData = fmData;
  }

  const temperatureDeviceIds = assetDevices.filter((v) => v.devcType === 'temperature').map((v) => v.devcUniqueId);
  if (temperatureDeviceIds.length > 0) {
    const temperatureData = temperaturePulseDatas.filter((d) => temperatureDeviceIds.includes(d.deviceId));
    if (temperatureData && temperatureData.length > 0) newAsset.lastTemperatureData = temperatureData;
  }

  const powermeterDeviceIds = assetDevices.filter((v) => v.devcType === 'powermeter').map((v) => v.devcUniqueId);
  if (powermeterDeviceIds.length > 0) {
    const powermeterData = powermeterPulseDatas.filter((d) => powermeterDeviceIds.includes(d.deviceId));
    if (powermeterData && powermeterData.length > 0) newAsset.lastPowermeterData = powermeterData;
  }

  // --- Status kesehatan sensor (generalized) ---
  // Tahap GPS sudah menetapkan 'online'/'offline'. Di sini status ditimpa
  // berdasarkan sensor yang MEMANG dimiliki aset (rpm/flowmeter/ae/temperature):
  //  - GPS segar + >=1 sensor yang diharapkan hilang/basi -> no-sensor (oranye)
  //  - GPS basi  + >=1 sensor masih segar -> no-gps (masalah GPS diprioritaskan)
  // Aset tanpa sensor kesehatan sama sekali (mis. hanya GPS) tetap online.
  // Sensor yang ditolak izin (role gak boleh liat) dikeluarin dari daftar "diharapkan" -
  // itu bukan sensor mati, cuma emang gak boleh dicek statusnya oleh role ini.
  const expectedHealthSensors = [...new Set(
    assetDevices.map((d) => d.devcType)
      .filter((t) => HEALTH_SENSOR_TYPES.includes(t))
      .filter((t) => !deniedSensorTypes.includes(t)),
  )];

  const isSensorFresh = (dataArr) => {
    if (!dataArr || dataArr.length === 0) return false;
    const latest = Math.max(...dataArr.map((d) => d.timestamp || 0));
    return (currTime - latest) < 600;
  };

  const sensorFreshness = {
    rpm: isSensorFresh(newAsset.lastRpmData),
    flowmeter: isSensorFresh(newAsset.lastFmData),
    ae: isSensorFresh(newAsset.lastAeData),
    temperature: isSensorFresh(newAsset.lastTemperatureData),
  };

  const isGpsOnline = newAsset.gpsDiffTime < 600;
  const hasExpectedSensor = expectedHealthSensors.length > 0;
  const allExpectedFresh = expectedHealthSensors.every((t) => sensorFreshness[t]);
  const anyExpectedFresh = expectedHealthSensors.some((t) => sensorFreshness[t]);

  if (isGpsOnline && hasExpectedSensor && !allExpectedFresh) {
    newAsset.shipStatus = 'no-sensor';
    const { iconConfig, markerIconUrl } = buildIconConfig(newAsset, 'no-sensor');
    newAsset.iconConfig = iconConfig;
    newAsset.markerIconUrl = markerIconUrl;
  } else if (!isGpsOnline && hasExpectedSensor && anyExpectedFresh) {
    newAsset.shipStatus = 'no-gps';
    const { iconConfig, markerIconUrl } = buildIconConfig(newAsset, 'no-gps');
    newAsset.iconConfig = iconConfig;
    newAsset.markerIconUrl = markerIconUrl;
  }

  return newAsset;
});

function getDeviceStatus(devices) {
  if (!devices || devices.length === 0) {
    return {
      hasExhaustTemperature: false,
      flowmeterType: 'none',
      flowmeterCount: 0,
      flowmeterPositions: [],
      flowmeterGroups: [],
      flowmeterPositionCounts: {
        PORT: 0,
        STARBOARD: 0,
        CENTER: 0,
      },
      hasPowermeter: false,
      powermeterCount: 0,
      aeCount: 0,
      rpmTypes: [],
      rpmCount: 0,
      hasRpm: false,
      hasFlowmeter: false,
      hasAe: false,
      hasTemperature: false,
      healthSensors: [],
    };
  }

  // Check for exhaust temperature sensor
  const hasExhaustTemperature = devices.some(
    (d) => d.devcType === 'temperature' && d.devcLabel === 'EXHAUST_TEMPERATURE',
  );

  // Analyze flowmeters
  const flowmeters = devices.filter((d) => d.devcType === 'flowmeter');
  const flowmeterCount = flowmeters.length;

  let flowmeterType = 'none';
  const flowmeterPositionCounts = {
    PORT: 0,
    STARBOARD: 0,
    CENTER: 0,
  };

  if (flowmeterCount > 0) {
    const hasIn = flowmeters.some((d) => d.devcLabel.includes('_IN'));
    const hasOut = flowmeters.some((d) => d.devcLabel.includes('_OUT'));

    if (hasIn && hasOut) {
      flowmeterType = 'in-out';
    } else if (hasIn) {
      flowmeterType = 'in-only';
    }
  }

  // Get flowmeter positions and count them
  flowmeters.forEach((fm) => {
    if (fm.devcLabel.startsWith('PORT')) {
      flowmeterPositionCounts.PORT += 1;
    } else if (fm.devcLabel.startsWith('STARBOARD')) {
      flowmeterPositionCounts.STARBOARD += 1;
    } else if (fm.devcLabel.startsWith('CENTER')) {
      flowmeterPositionCounts.CENTER += 1;
    }
  });

  const flowmeterPositions = Object.keys(flowmeterPositionCounts).filter(
    (pos) => flowmeterPositionCounts[pos] > 0,
  );

  // Flowmeter groups — label-driven, tak terbatas posisi engine.
  // Nama group = devcLabel tanpa suffix _IN/_OUT (PORT, STARBOARD, CENTER, AE4, AE_INLINE, ...).
  // Satu group bisa punya IN dan/atau OUT (in-out -> bisa hitung net). BUNKER dikecualikan.
  const flowmeterGroupMap = {};
  flowmeters.forEach((fm) => {
    const label = fm.devcLabel || '';
    const name = label.replace(/_(IN|OUT)$/, '');
    if (!name || EXCLUDED_FLOWMETER_GROUPS.includes(name.toUpperCase())) return;
    if (!flowmeterGroupMap[name]) {
      flowmeterGroupMap[name] = { name, hasIn: false, hasOut: false };
    }
    if (label.endsWith('_OUT')) flowmeterGroupMap[name].hasOut = true;
    else flowmeterGroupMap[name].hasIn = true;
  });
  const flowmeterGroups = Object.values(flowmeterGroupMap);

  // Analyze powermeters
  const powermeters = devices.filter((d) => d.devcType === 'powermeter');
  const powermeterCount = powermeters.length;
  const hasPowermeter = powermeterCount > 0;

  // Kapabilitas sensor per-aset (dari device list) untuk status no-sensor.
  const hasRpm = devices.some((d) => d.devcType === 'rpm');
  const hasAe = devices.some((d) => d.devcType === 'ae');
  const hasTemperature = devices.some((d) => d.devcType === 'temperature');
  const hasFlowmeter = flowmeterCount > 0;
  const sensorPresence = {
    rpm: hasRpm,
    flowmeter: hasFlowmeter,
    ae: hasAe,
    temperature: hasTemperature,
  };
  const healthSensors = HEALTH_SENSOR_TYPES.filter((type) => sensorPresence[type]);

  // aeCount dan rpmTypes diderive setelah getLastData — lihat deriveDeviceStatusFromPulse()
  return {
    hasExhaustTemperature,
    flowmeterType,
    flowmeterCount,
    flowmeterPositions,
    flowmeterGroups,
    flowmeterPositionCounts,
    hasPowermeter,
    powermeterCount,
    aeCount: 0,
    rpmTypes: [],
    rpmTypesCount: 0,
    hasRpm,
    hasFlowmeter,
    hasAe,
    hasTemperature,
    healthSensors,
  };
}

function deriveDeviceStatusFromPulse(assets) {
  return assets.map((asset) => {
    const deviceStatus = { ...(asset.deviceStatus || {}) };

    if (asset.lastAeData && asset.lastAeData.length > 0) {
      const maxNo = asset.lastAeData.reduce((max, item) => (item.no > max ? item.no : max), -1);
      deviceStatus.aeCount = maxNo > -1 ? maxNo + 1 : 0;
    }

    // rpmTypes diturunkan dari tipe yang muncul di pulse RPM:
    // single engine -> cuma CENTER; twin -> PORT/STARBOARD. Tanpa hardcode massId.
    const rpmDevice = (asset.devices || []).find((d) => d.devcType === 'rpm');
    if (rpmDevice && asset.lastRpmData && asset.lastRpmData.length > 0) {
      const uniqueRpmTypes = new Set(asset.lastRpmData.map((item) => item.type));
      deviceStatus.rpmTypes = Array.from(uniqueRpmTypes);
      deviceStatus.rpmTypesCount = deviceStatus.rpmTypes.length;
    }

    return { ...asset, deviceStatus };
  });
}

const mutations = {
  UPDATE_ASSET_STATUS(state, { id, status }) {
    const asset = state.assets.find((asset) => asset.id === id);
    if (asset) {
      asset.status = status;
    }
  },
  UPDATE_ASSET_VISIBILITY(state, { massId, isVisible }) {
    const asset = state.assets.find((asset) => asset.massId === massId);
    if (asset) {
      asset.isVisible = isVisible;
    }
  },
  SET_ALL_ASSETS_VISIBILITY(state, isVisible) {
    state.assets.forEach((asset) => {
      asset.isVisible = isVisible;
    });
  },
  UPDATE_ASSET_TRAIL(state, { massId, trail }) {
    const asset = state.assets.find((asset) => asset.massId === massId);
    if (asset) {
      asset.trail = trail;
    }
  },
  SET_ASSETS(state, assets) {
    state.assets = assets;
  },
  SET_LIVE_TRACKING_ID(state, id) {
    state.liveTrackingId = id;
  },
  SET_LIVE_TRACKING_ACTIVE(state, status) {
    state.isLiveTrackingActive = status;
  },
};

const actions = {
  async getAssetsData({ commit }) {
    const res = await AssetSvc.getAssets();
    const assetsData = res?.data?.data ?? [];

    const processedAssets = assetsData.map((asset) => {
      const newAsset = { ...asset };
      if (newAsset.massDevice) {
        newAsset.devices = newAsset.massDevice;
        delete newAsset.massDevice;
      } else {
        newAsset.devices = [];
      }
      return newAsset;
    });

    commit('SET_ASSETS', processedAssets);
    return processedAssets;
  },

  async getAssetsDevices({ commit, state }) {
    const currAssets = Object.values(state.assets);

    const finalAssetsPromises = currAssets.map(async (asset) => {
      let deviceData = asset.devices || [];

      // If devices are missing, fetch them ONCE.
      if (deviceData.length === 0) {
        try {
          const param = { findField: 'devcMassId', findValue: asset.massId, max: 100 };
          const response = await DeviceSvc.getDevicesByAssetId(param);
          deviceData = response?.data?.data || [];
        } catch (error) {
          console.error(`Failed to fetch devices for asset ${asset.massId}`, error);
          deviceData = []; // Ensure it's an array on failure
        }
      }

      // Now, with the final deviceData (either from master or fetched), calculate status.
      const deviceStatus = await getDeviceStatus(deviceData);

      return {
        ...asset,
        devices: deviceData, // Update with fetched data if it was missing
        deviceStatus,
      };
    });

    const finalAssets = await Promise.all(finalAssetsPromises);

    commit('SET_ASSETS', finalAssets);
    // console.log('[asset.js] Devices processed and status calculated:', finalAssets);
    return finalAssets;
  },

  stopLiveTracking({ commit, state }) {
    if (state.liveTrackingId) {
      clearTimeout(state.liveTrackingId);
      commit('SET_LIVE_TRACKING_ID', null);
    }
    commit('SET_LIVE_TRACKING_ACTIVE', false);
    // Live tracking stopped (log removed)
  },

  async getLastData({ commit, state, dispatch }, mode = 'standard') {
    dispatch('stopLiveTracking');
    if (!state.isLiveTrackingActive) {
      commit('SET_LIVE_TRACKING_ACTIVE', true);
    }

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      if (!state.isLiveTrackingActive) {
        resolve();
        return;
      }

      const currTime = Math.floor(Date.now() / 1000);
      const currAssets = Object.values(state.assets);
      // Fetching last data for assets (debug log removed)
      // console.log('[getLastData] Current assets:', JSON.stringify(currAssets, null, 2));

      const isSatMode = mode === 'sat';

      // --- sat Mode: Only fetch GPS and RPM using massSateliteId ---
      if (isSatMode) {
        // Get satellite IDs from assets
        const satelliteGpsIds = currAssets
          .filter((asset) => asset.massSateliteId)
          .map((asset) => asset.massSateliteId);

        const satelliteRpmIds = currAssets
          .filter((asset) => asset.massSateliteId)
          .map((asset) => asset.massSateliteId);

        // Fetch GPS data
        const gpsPulseDatas = (satelliteGpsIds.length > 0
          ? await PulseSvc.getPulse(satelliteGpsIds, 'gps', 'sat')
          : []) || []; // null (ditolak izin) -> [] biar gak error di mapGpsPulseToAssets

        // Fetch RPM data
        const rpmPulseDatas = (satelliteRpmIds.length > 0
          ? await PulseSvc.getPulse(satelliteRpmIds, 'rpm', 'sat')
          : []) || [];

        // Fetch AE data
        const aePulseDatas = (satelliteRpmIds.length > 0
          ? await PulseSvc.getPulse(satelliteRpmIds, 'ae', 'sat')
          : []) || [];

        // Map GPS data to assets (pass 'sat' mode)
        const assetsWithGps = await mapGpsPulseToAssets(currAssets, new Map(), gpsPulseDatas, currTime, 'sat');

        // Map RPM and AE data to assets (pass isSatMode = true)
        const finalMappedAssets = mapOtherPulseToAssets(
          assetsWithGps,
          new Map(),
          rpmPulseDatas,
          aePulseDatas,
          [], // fmPulseDatas
          [], // temperaturePulseDatas
          [], // powermeterPulseDatas
          currTime,
          true, // isSatMode
        );

        commit('SET_ASSETS', deriveDeviceStatusFromPulse(finalMappedAssets));

        // DISABLED: Live tracking recursive call (temporary)
        // if (state.isLiveTrackingActive) {
        //   const now = new Date();
        //   const nextTarget = new Date(now);
        //   nextTarget.setSeconds(59);
        //   nextTarget.setMilliseconds(0);
        //   if (now.getTime() >= nextTarget.getTime()) {
        //     nextTarget.setMinutes(nextTarget.getMinutes() + 1);
        //   }

        //   const delayMs = nextTarget.getTime() - now.getTime();

        //   const timeoutId = setTimeout(() => {
        //     dispatch('getLastData', mode);
        //   }, delayMs);

        //   commit('SET_LIVE_TRACKING_ID', timeoutId);
        //   resolve();
        // } else {
        //   console.log('[getLastData] Live tracking stopped, recursion ended.');
        //   resolve();
        // }
        resolve();
        return;
      }

      // --- Standard Mode: Full device mapping and all data types ---
      const massDevicesMap = new Map();
      currAssets.forEach((asset) => {
        massDevicesMap.set(asset.massId, asset.devices || []);
      });

      // --- Stage 1: GPS ---
      const gpsIds = currAssets.flatMap((asset) => (asset.devices || []).filter((d) => d.devcType === 'gps').map((d) => d.devcUniqueId));
      const gpsPulseDatas = (gpsIds.length > 0 ? await PulseSvc.getPulse(gpsIds, 'gps', mode) : []) || [];

      const assetsWithGps = await mapGpsPulseToAssets(currAssets, massDevicesMap, gpsPulseDatas, currTime, mode);

      commit('SET_ASSETS', assetsWithGps);

      if (!state.isLiveTrackingActive) return;

      // --- Stage 2: Other Devices ---
      const otherDevicesByType = new Map();
      const excludedPulseTypes = ['gps', 'power'];
      currAssets.forEach((asset) => {
        (asset.devices || []).forEach((device) => {
          if (!excludedPulseTypes.includes(device.devcType)) {
            if (!otherDevicesByType.has(device.devcType)) {
              otherDevicesByType.set(device.devcType, []);
            }
            otherDevicesByType.get(device.devcType).push(device.devcUniqueId);
          }
        });
      });

      const pulsePromises = [];
      const types = [];
      for (const [type, ids] of otherDevicesByType.entries()) {
        const uniqueIds = [...new Set(ids)];
        if (uniqueIds.length > 0) {
          types.push(type);
          pulsePromises.push(PulseSvc.getPulse(uniqueIds, type));
        }
      }

      const pulseResults = await Promise.all(pulsePromises);

      const pulseDataMap = new Map();
      pulseResults.forEach((result, index) => {
        pulseDataMap.set(types[index], result);
      });

      // pulseDataMap value === null artinya endpoint-nya ditolak izin (errorCode 412),
      // beda kasus sama tipe yang emang gak ada device-nya sama sekali.
      const deniedSensorTypes = HEALTH_SENSOR_TYPES.filter((type) => pulseDataMap.has(type) && pulseDataMap.get(type) === null);

      const aePulseDatas = pulseDataMap.get('ae') || [];
      const rpmPulseDatas = pulseDataMap.get('rpm') || [];
      const fmPulseDatas = pulseDataMap.get('flowmeter') || [];
      const temperaturePulseDatas = pulseDataMap.get('temperature') || [];
      const powermeterPulseDatas = pulseDataMap.get('powermeter') || [];

      const finalMappedAssets = mapOtherPulseToAssets(
        assetsWithGps,
        massDevicesMap,
        aePulseDatas,
        rpmPulseDatas,
        fmPulseDatas,
        temperaturePulseDatas,
        powermeterPulseDatas,
        currTime,
        false, // isSatMode
        deniedSensorTypes,
      );

      commit('SET_ASSETS', deriveDeviceStatusFromPulse(finalMappedAssets));

      // DISABLED: Live tracking recursive call (temporary)
      // if (state.isLiveTrackingActive) {
      //   const now = new Date();
      //   const nextTarget = new Date(now);
      //   nextTarget.setSeconds(59);
      //   nextTarget.setMilliseconds(0);
      //   if (now.getTime() >= nextTarget.getTime()) {
      //     nextTarget.setMinutes(nextTarget.getMinutes() + 1);
      //   }

      //   const delayMs = nextTarget.getTime() - now.getTime();

      //   const timeoutId = setTimeout(() => {
      //     dispatch('getLastData', mode);
      //   }, delayMs);

      //   commit('SET_LIVE_TRACKING_ID', timeoutId);
      //   resolve();
      // } else {
      //   console.log('[getLastData] Live tracking stopped, recursion ended.');
      //   resolve();
      // }
      resolve();
    });
  },

  async fetchSummary({ commit, state }, {
    start, end, interval, massId,
  }) {
    const assets = Object.values(state.assets);

    const firstQueryTimestamp = moment(start).unix();
    const lastQueryTimestamp = moment(end).unix();

    const currentAsset = assets.find((asset) => {
      const match = String(asset.massId) === String(massId);
      return match;
    });

    if (!currentAsset) {
      console.error(`[fetchSummary] Asset with massId ${massId} not found.`);
      return assets;
    }

    let existingDevcTypes = [];
    if (currentAsset.devices && currentAsset.devices.length > 0) {
      const uniqueTypes = new Set(currentAsset.devices.map((d) => d.devcType));
      existingDevcTypes = Array.from(uniqueTypes);
    }

    existingDevcTypes = existingDevcTypes.filter((type) => type !== 'gateway' && type !== 'limitswitch' && type !== 'power');

    if (existingDevcTypes.length === 0) {
      console.warn(`[fetchSummary] No devices found on asset ${massId}. Summary fetch skipped.`);
      return assets;
    }

    const aggregatedUnit = interval.toUpperCase();

    // Satu request untuk seluruh rentang dengan semua device type sekaligus —
    // backend meng-cache per hari, mecah rentang malah lambat.
    const response = await SummarySvc.getSummary({
      start: firstQueryTimestamp,
      end: lastQueryTimestamp,
      aggregatedUnit,
      devcType: existingDevcTypes,
      devcMassId: massId,
      showDetails: true,
    });

    const summaryData = response?.data?.data || {};

    const mappedAssets = assets.map((asset) => {
      if (String(asset.massId) === String(massId)) {
        const mappedCoordinatesData = processSummarySeriesData(summaryData, firstQueryTimestamp, lastQueryTimestamp);

        // Koordinat sekarang dipegang fetchShipTrail biar polyline cuma punya
        // satu penulis. Datanya gak dibuang — dituang ke cache titik dot trail,
        // jadi klik dot di halaman ini gak perlu request lagi.
        const pointDetail = {};
        mappedCoordinatesData.forEach((p) => {
          if (p.timestamp) pointDetail[p.timestamp] = p;
        });
        const updatedAsset = {
          ...asset,
          summary: summaryData,
          // Penanda rentang yang sama kayak fetchShipSummary, biar
          // ensureShipSummary gak narik rollup lagi di halaman yang
          // summary-nya udah lengkap.
          summaryRangeKey: `${firstQueryTimestamp}-${lastQueryTimestamp}`,
          trailPointDetail: { ...(asset.trailPointDetail || {}), ...pointDetail },
        };

        // Bucket paling akhir itu jam berjalan, dan backend belum ngagregasi jam
        // itu — jadi hampir selalu kosong buat rentang yang ujungnya "sekarang".
        // Ambil bucket terisi terakhir, bukan bucket terakhir. Kalau enggak,
        // lastLatLng gak keisi -> createShipMarker balik null -> addShipsToMap
        // kena `if (!marker) return` -> marker DAN trail dua-duanya gak kegambar.
        const lastData = [...mappedCoordinatesData]
          .reverse()
          .find((d) => d?.gpsData?.additional?.LAST_dlgpLatitude_4
            && d?.gpsData?.additional?.LAST_dlgpLongitude_5);
        if (lastData?.gpsData?.additional?.LAST_dlgpLatitude_4 && lastData?.gpsData?.additional?.LAST_dlgpLongitude_5) {
          updatedAsset.lastGpsData = lastData.gpsData;
          // eslint-disable-next-line no-undef
          updatedAsset.lastLatLng = L.latLng(lastData.gpsData?.additional?.LAST_dlgpLatitude_4, lastData.gpsData?.additional?.LAST_dlgpLongitude_5);
        }
        return updatedAsset;
      }
      return asset;
    });

    commit('SET_ASSETS', mappedAssets);
    return mappedAssets;
  },

  // Summary dari endpoint rollup: cuma blok agregat (~5 KB), nol deret waktu.
  // `fetchSummary` di atas SENGAJA dibiarin hidup — Datalog & Bunker Dashboard
  // masih makan dari situ dan belum boleh berubah.
  async fetchSummaryRollup({ commit, state }, payload) {
    const { massId, start, end } = payload || {};
    const asset = Object.values(state.assets).find((a) => String(a.massId) === String(massId));
    if (!asset) {
      console.error(`[fetchSummaryRollup] Asset with massId ${massId} not found.`);
      return;
    }

    const startUnix = moment(start).unix();
    const endUnix = moment(end).unix();
    const rangeKey = `${startUnix}-${endUnix}`;

    // Aturan sama kayak fetchSummary legacy, plus powermeter dikecualiin (rollup
    // belum ngelayanin, jalurnya sendiri di bawah). JANGAN di-hardcode: hardcode
    // ['gps','rpm','flowmeter','ae'] bikin blok temperature gak ikut balik dan
    // baris suhu di Report ilang diem-diem.
    const excluded = ['gateway', 'limitswitch', 'power', 'powermeter'];
    const devcType = Array.from(new Set((asset.devices || []).map((d) => d.devcType)))
      .filter((type) => !excluded.includes(type));

    if (devcType.length === 0) {
      console.warn(`[fetchSummaryRollup] No devices found on asset ${massId}. Summary fetch skipped.`);
      return;
    }

    const res = await SummarySvc.getAssetSummaryRollup({
      devcMassId: asset.massId, devcType, start: startUnix, end: endUnix,
    });
    const body = res?.data?.data || {};

    // Tipe di sini GAK ADA di body, dan itu beda dari nol.
    const unavailable = res?.data?.meta?.unavailable || [];
    if (unavailable.length > 0) {
      console.warn(`[fetchSummaryRollup] rollup belum ngelayanin: ${unavailable.join(', ')} — jangan dianggap nol`);
    }

    // powermeter belum dilayani rollup tapi FmsReport masih baca bloknya.
    // Endpoint lama dijadwalkan mati: gagal = blok kosong, bukan halaman rusak.
    if ((asset.devices || []).some((d) => d.devcType === 'powermeter')) {
      try {
        const pm = await SummarySvc.getSummary({
          devcType: ['powermeter'],
          devcMassId: asset.massId,
          start: startUnix,
          end: endUnix,
          aggregatedUnit: 'HOUR',
          aggregatedLength: 1,
          showDetails: false,
        });
        if (pm?.data?.data?.powermeter) body.powermeter = pm.data.data.powermeter;
      } catch (e) {
        console.warn('[fetchSummaryRollup] powermeter gagal ditarik dari endpoint lama, bloknya dikosongin', e);
      }
    }

    // baca ulang state setelah await biar gak nimpa hasil fetch paralel lain
    const updated = Object.values(state.assets).map((a) => (String(a.massId) !== String(massId) ? a : {
      ...a, summary: body, summaryRangeKey: rangeKey, summarySource: 'rollup',
    }));
    commit('SET_ASSETS', updated);
  },

  // Deret waktu buat chart, ditempel ke summary yang udah ditarik
  // fetchSummaryRollup. Balikin daftar tipe yang gagal biar halaman bisa
  // nampilin toast — store gak nyentuh UI.
  async fetchSummarySeries({ commit, state }, payload) {
    const {
      massId, start, end, interval,
    } = payload || {};
    const asset = Object.values(state.assets).find((a) => String(a.massId) === String(massId));
    if (!asset?.summary) return [];

    const startUnix = moment(start).unix();
    const endUnix = moment(end).unix();
    const rangeKey = `${startUnix}-${endUnix}`;

    const { data, failedTypes } = await fetchSummarySeriesData({
      devices: asset.devices || [],
      start: startUnix,
      end: endUnix,
      interval,
      rpmTypes: deriveRpmTypesFromSummary(asset.summary),
      aeCount: deriveAeCountFromSummary(asset.summary),
    });

    // baca ulang state setelah await: kalau user keburu ganti aset/rentang,
    // hasil yang telat DIBUANG, bukan nimpa yang baru.
    const fresh = Object.values(state.assets).find((a) => String(a.massId) === String(massId));
    if (!fresh?.summary || fresh.summaryRangeKey !== rangeKey) return failedTypes;

    // Nyusun objek gabungan lalu commit SEKALI. Nambah key `data` ke objek yang
    // udah reaktif gak kebaca Vue 2 — ini udah kena sekali.
    const merged = {};
    Object.keys(fresh.summary).forEach((type) => {
      merged[type] = data[type]
        ? { ...fresh.summary[type], data: data[type] }
        : fresh.summary[type];
    });

    const updated = Object.values(state.assets).map((a) => (String(a.massId) !== String(massId) ? a : {
      ...a, summary: merged,
    }));
    commit('SET_ASSETS', updated);
    return failedTypes;
  },

  // Geometry trail doang — /summary/trail ~56x lebih kecil per titik daripada
  // /summary/aggregated/period. Stats-nya nyusul lewat fetchShipSummary.
  async fetchShipTrail({ commit, state, rootGetters }, payload) {
    const {
      massId, days = 1, start, end,
    } = payload || {};
    const asset = Object.values(state.assets).find((a) => String(a.massId) === String(massId));
    if (!asset) return;

    const range = trailRangeFrom({
      start, end, days, lastTimestamp: asset.lastGpsData?.timestamp,
    });
    if (!range) return;

    let trail;
    if (rootGetters['auth/isGpsViewer']) {
      trail = await fetchGpsOnlyTrail(asset, range.start, range.end);
    } else {
      const res = await SummarySvc.getTrail({
        devcMassId: asset.massId, start: range.start, end: range.end,
      });
      trail = parseTrailResponse(res?.data?.data);
    }

    // baca ulang state setelah await biar gak nimpa hasil fetch paralel lain
    const updated = Object.values(state.assets).map((a) => (String(a.massId) !== String(massId) ? a : {
      ...a, coordinates: trail.coordinates, coordinatesData: trail.coordinatesData,
    }));
    commit('SET_ASSETS', updated);
  },

  // Trail stats doang (9 field skalar yang dibaca buildTrailStats). Endpoint
  // rollup gak bawa blok `data`, jadi jauh lebih cepat dari getSummary.
  // Catatan: channel PORT/STARBOARD di rollup kemungkinan ketuker —
  // lihat spec 2026-08-29, keputusan user: pakai apa adanya.
  async fetchShipSummary({ commit, state }, payload) {
    const {
      massId, days = 1, start, end,
    } = payload || {};
    const asset = Object.values(state.assets).find((a) => String(a.massId) === String(massId));
    if (!asset) return;

    const range = trailRangeFrom({
      start, end, days, lastTimestamp: asset.lastGpsData?.timestamp,
    });
    if (!range) return;

    const rangeKey = `${range.start}-${range.end}`;
    if (asset.summaryRangeKey === rangeKey) return;

    const res = await SummarySvc.getAssetSummaryRollup({
      devcMassId: asset.massId,
      devcType: ['gps', 'rpm', 'flowmeter', 'ae'],
      start: range.start,
      end: range.end,
    });

    // baca ulang state setelah await biar gak nimpa hasil fetch paralel lain
    const updated = Object.values(state.assets).map((a) => (String(a.massId) !== String(massId) ? a : {
      ...a, summary: res?.data?.data || {}, summaryRangeKey: rangeKey,
    }));
    commit('SET_ASSETS', updated);
  },

  // Detail satu titik dot trail. Satu titik = satu rentang sejam, dan endpoint
  // rollup melayani rentang itu — jadi jalur map lepas total dari
  // /summary/aggregated/period yang bakal dibuang backend.
  async fetchTrailPointDetail({ state }, payload) {
    const { massId, timestamp } = payload || {};
    const asset = Object.values(state.assets).find((a) => String(a.massId) === String(massId));
    if (!asset || !timestamp) return undefined;
    if (asset.trailPointDetail?.[timestamp]) return asset.trailPointDetail[timestamp];

    const { start, end } = hourWindow(timestamp);
    const res = await SummarySvc.getAssetSummaryRollup({
      devcMassId: asset.massId,
      devcType: ['gps', 'rpm', 'flowmeter', 'ae'],
      start,
      end,
    });

    // gak commit ke store lagi - hasilnya cuma cache tampilan, dipegang MapService
    // biar SET_ASSETS gak trigger rebuild map & nutup popup dot yang lagi kebuka
    return rollupPointDetail(res?.data?.data || {});
  },

  updateAssetStatus({ commit }, payload) {
    commit('UPDATE_ASSET_STATUS', payload);
  },

  async getAssetsFromAPI({ dispatch, state }) {
    dispatch('stopLiveTracking');

    await dispatch('getAssetsData');
    await dispatch('getAssetsDevices');
    await dispatch('getLastData');

    const initialAssets = state.assets.filter((asset) => asset.lastGpsData);
    return initialAssets;
  },

  async fetchAndReconcileAssets({ dispatch, commit }) {
    const [apiAssets, visibilityMap] = await Promise.all([
      dispatch('getAssetsFromAPI'),
      getAllVisibilityStatuses(),
    ]);

    const reconciledAssets = apiAssets.map((asset) => {
      if (visibilityMap.has(asset.massId)) {
        asset.isVisible = visibilityMap.get(asset.massId);
      } else {
        asset.isVisible = true;
      }
      return asset;
    });

    commit('SET_ASSETS', reconciledAssets);

    const allStatuses = reconciledAssets.map(({ massId, isVisible }) => ({ massId, isVisible }));
    await saveAllVisibilityStatuses(allStatuses);

    return reconciledAssets;
  },

  async updateAssetVisibility({ commit }, payload) {
    commit('UPDATE_ASSET_VISIBILITY', payload);
    await saveVisibilityStatus(payload.massId, payload.isVisible);
  },

  async setAllAssetsVisibility({ commit, state }, isVisible) {
    commit('SET_ALL_ASSETS_VISIBILITY', isVisible);
    const allStatuses = state.assets.map((asset) => ({
      massId: asset.massId,
      isVisible,
    }));
    await saveAllVisibilityStatuses(allStatuses);
  },

  // Recompute every asset's icon config from the current global icon settings
  // (style + size) and drop cached Leaflet icons so maps rebuild them.
  applyIconSettings({ commit, state }) {
    const assets = state.assets.map((asset) => {
      if (!asset.shipStatus) return asset;
      const { iconConfig, markerIconUrl } = buildIconConfig(asset, asset.shipStatus);
      return {
        ...asset,
        iconConfig,
        markerIconUrl,
        icon: null,
        markerIcon: null,
        headingIcon: null,
      };
    });
    commit('SET_ASSETS', assets);
  },

  // eslint-disable-next-line no-unused-vars
  updateAssetTrail({ commit }, { massId, trail }) {
    const assets = Object.values(state.assets);

    assets.map((asset) => {
      if (asset.massId === massId) {
        asset.trail = trail;
      }
      return asset;
    });

    commit('SET_ASSETS', assets);
  },

  updateAssetDotTrail({ commit }, { massId, dotTrail }) {
    const assets = Object.values(state.assets);

    assets.map((asset) => {
      if (asset.massId === massId) {
        asset.dotTrail = dotTrail;
      }
      return asset;
    });

    commit('SET_ASSETS', assets);
  },

  resetAssetsData({ dispatch, commit }) {
    dispatch('stopLiveTracking');
    commit('SET_ASSETS', []);
    return [];
  },
};

const getters = {
  assets: (state) => state.assets,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
